import React, { useState } from 'react';
import { useDeals } from '../context/DealsContext';
import { useQuota } from '../context/QuotaContext';
import { FiTrendingUp, FiTarget, FiDollarSign, FiAward, FiCheckSquare } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RecentDeals from '../components/RecentDeals';
import QuotaProgress from '../components/QuotaProgress';
import GoalTracker from '../components/GoalTracker';
import WinTheDay from '../components/WinTheDay';
import MetricCard from '../components/MetricCard';

const Dashboard = () => {
  const { deals, getQualityMetrics } = useDeals();
  const { quota, getAdjustedQuota, getPipelineGoal } = useQuota();
  const [showWinTheDay, setShowWinTheDay] = useState(() => {
    return localStorage.getItem('showWinTheDay') !== 'false';
  });

  const metrics = getQualityMetrics();

  // Get current quota and pipeline goal
  const currentQuota = getAdjustedQuota();
  const pipelineGoal = getPipelineGoal();

  // Calculate how far we are from the pipeline goal
  const pipelinePercentage = metrics.totalValue > 0 ? Math.min(100, (metrics.totalValue / pipelineGoal) * 100) : 0;

  // Generate chart data for the metric cards
  const getChartData = (type) => {
    switch (type) {
      case 'totalDeals':
        // Distribution of deals by stage
        const stageDistribution = deals.reduce((acc, deal) => {
          acc[deal.stage] = (acc[deal.stage] || 0) + 1;
          return acc;
        }, {});
        return Object.entries(stageDistribution).map(([stage, count]) => ({
          name: stage,
          value: count
        }));
      case 'avgQuality':
        // Quality scores distribution in ranges
        const qualityRanges = [
          { name: '9-10', min: 9, max: 10 },
          { name: '8-9', min: 8, max: 9 },
          { name: '7-8', min: 7, max: 8 },
          { name: '6-7', min: 6, max: 7 },
          { name: '5-6', min: 5, max: 6 },
          { name: '0-5', min: 0, max: 5 }
        ];
        return qualityRanges
          .map(range => ({
            name: range.name,
            value: deals.filter(
              deal => deal.qualityScore >= range.min && deal.qualityScore < range.max
            ).length
          }))
          .filter(item => item.value > 0);
      case 'strongDeals':
        // Clear labels for better chart readability
        const strongCount = metrics.strongDeals;
        const weakCount = metrics.totalDeals - metrics.strongDeals;
        return [
          { name: 'Strong', value: strongCount },
          { name: 'Needs Info', value: weakCount }
        ];
      case 'pipeline':
        // Shorter stage names for better chart visibility
        const pipelineByStage = deals.reduce((acc, deal) => {
          if (deal.stage !== 'Closed Lost') {
            // Use shorter names for chart labels
            const stageName = 
              deal.stage === 'Qualification' ? 'Qual' : 
              deal.stage === 'Discovery' ? 'Disc' :
              deal.stage === 'Proposal' ? 'Prop' :
              deal.stage === 'Negotiation' ? 'Neg' :
              deal.stage === 'Closed Won' ? 'Won' : deal.stage;
            acc[stageName] = (acc[stageName] || 0) + deal.value;
          }
          return acc;
        }, {});
        return Object.entries(pipelineByStage).map(([stage, value]) => ({
          name: stage,
          value: value
        }));
      default:
        return [];
    }
  };

  // Determine color based on value
  const getMetricColor = (type, value) => {
    switch (type) {
      case 'avgQuality':
        if (value >= 8) return 'green';
        if (value >= 6) return 'amber';
        return 'red';
      case 'strongDeals':
        const percentage = metrics.totalDeals > 0 ? (value / metrics.totalDeals) * 100 : 0;
        if (percentage >= 70) return 'green';
        if (percentage >= 40) return 'amber';
        return 'red';
      case 'pipeline':
        if (pipelinePercentage >= 75) return 'green';
        if (pipelinePercentage >= 50) return 'amber';
        return 'red';
      default:
        return 'blue';
    }
  };

  const statCards = [
    {
      title: 'Total Deals',
      value: metrics.totalDeals,
      icon: FiTarget,
      color: 'blue',
      chartData: getChartData('totalDeals')
    },
    {
      title: 'Avg Quality Score',
      value: metrics.avgQuality,
      icon: FiAward,
      color: getMetricColor('avgQuality', parseFloat(metrics.avgQuality)),
      chartData: getChartData('avgQuality')
    },
    {
      title: 'Strong Deals',
      value: metrics.strongDeals,
      icon: FiCheckSquare,
      color: getMetricColor('strongDeals', metrics.strongDeals),
      chartData: getChartData('strongDeals')
    },
    {
      title: 'Pipeline vs Goal',
      value: `$${(metrics.totalValue / 1000).toFixed(0)}K`,
      subvalue: `Goal: $${(pipelineGoal / 1000).toFixed(0)}K (${Math.round(pipelinePercentage)}%)`,
      icon: FiDollarSign,
      color: getMetricColor('pipeline'),
      chartData: getChartData('pipeline')
    }
  ];

  const toggleWinTheDay = () => {
    const newValue = !showWinTheDay;
    setShowWinTheDay(newValue);
    localStorage.setItem('showWinTheDay', newValue);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </div>
          <div className="text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-full border border-green-200 dark:border-green-800/50 capitalize">
            {quota.period} Quota: ${quota.amount.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {statCards.map((stat, index) => (
          <MetricCard
            key={index}
            title={stat.title}
            value={stat.value}
            subvalue={stat.subvalue}
            icon={stat.icon}
            color={stat.color}
            chartData={stat.chartData}
          />
        ))}
      </div>

      {showWinTheDay && <WinTheDay />}
      <div className="flex justify-end">
        <button
          onClick={toggleWinTheDay}
          className="text-sm text-blue-700 dark:text-blue-400 hover:underline"
        >
          {showWinTheDay ? 'Hide Win The Day' : 'Show Win The Day'}
        </button>
      </div>

      <GoalTracker />
      <QuotaProgress />

      <div className="bg-white dark:bg-gray-800 p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Deal Strength Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Primary Purchaser */}
          <div
            className={
              metrics.withPurchaserContact >= (metrics.totalDeals * 0.7)
                ? "bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800/50"
                : metrics.withPurchaserContact >= (metrics.totalDeals * 0.4)
                ? "bg-amber-50 dark:bg-amber-900/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800/50"
                : "bg-red-50 dark:bg-red-900/30 p-4 rounded-lg border border-red-200 dark:border-red-800/50"
            }
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Primary Purchaser</h4>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{metrics.withPurchaserContact}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={
                  metrics.withPurchaserContact >= (metrics.totalDeals * 0.7)
                    ? "bg-green-600 dark:bg-green-500 h-2 rounded-full"
                    : metrics.withPurchaserContact >= (metrics.totalDeals * 0.4)
                    ? "bg-amber-600 dark:bg-amber-500 h-2 rounded-full"
                    : "bg-red-600 dark:bg-red-500 h-2 rounded-full"
                }
                style={{ width: `${metrics.totalDeals > 0 ? (metrics.withPurchaserContact / metrics.totalDeals) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-2">
              {metrics.totalDeals > 0 ? Math.round((metrics.withPurchaserContact / metrics.totalDeals) * 100) : 0}% of deals have primary purchaser info
            </p>
          </div>

          {/* Secondary Purchaser */}
          <div
            className={
              metrics.withSecondaryContact >= (metrics.totalDeals * 0.7)
                ? "bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800/50"
                : metrics.withSecondaryContact >= (metrics.totalDeals * 0.4)
                ? "bg-amber-50 dark:bg-amber-900/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800/50"
                : "bg-red-50 dark:bg-red-900/30 p-4 rounded-lg border border-red-200 dark:border-red-800/50"
            }
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Secondary Purchaser</h4>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{metrics.withSecondaryContact}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={
                  metrics.withSecondaryContact >= (metrics.totalDeals * 0.7)
                    ? "bg-green-600 dark:bg-green-500 h-2 rounded-full"
                    : metrics.withSecondaryContact >= (metrics.totalDeals * 0.4)
                    ? "bg-amber-600 dark:bg-amber-500 h-2 rounded-full"
                    : "bg-red-600 dark:bg-red-500 h-2 rounded-full"
                }
                style={{ width: `${metrics.totalDeals > 0 ? (metrics.withSecondaryContact / metrics.totalDeals) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-2">
              {metrics.totalDeals > 0 ? Math.round((metrics.withSecondaryContact / metrics.totalDeals) * 100) : 0}% of deals have secondary purchaser info
            </p>
          </div>

          {/* Projected Close Date */}
          <div
            className={
              metrics.withCloseDate >= (metrics.totalDeals * 0.7)
                ? "bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800/50"
                : metrics.withCloseDate >= (metrics.totalDeals * 0.4)
                ? "bg-amber-50 dark:bg-amber-900/30 p-4 rounded-lg border border-amber-200 dark:border-amber-800/50"
                : "bg-red-50 dark:bg-red-900/30 p-4 rounded-lg border border-red-200 dark:border-red-800/50"
            }
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Projected Close Date</h4>
              <span className="text-lg font-bold text-gray-900 dark:text-white">{metrics.withCloseDate}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={
                  metrics.withCloseDate >= (metrics.totalDeals * 0.7)
                    ? "bg-green-600 dark:bg-green-500 h-2 rounded-full"
                    : metrics.withCloseDate >= (metrics.totalDeals * 0.4)
                    ? "bg-amber-600 dark:bg-amber-500 h-2 rounded-full"
                    : "bg-red-600 dark:bg-red-500 h-2 rounded-full"
                }
                style={{ width: `${metrics.totalDeals > 0 ? (metrics.withCloseDate / metrics.totalDeals) * 100 : 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-2">
              {metrics.totalDeals > 0 ? Math.round((metrics.withCloseDate / metrics.totalDeals) * 100) : 0}% of deals have projected close dates
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <RecentDeals />
      </div>
    </div>
  );
};

export default Dashboard;